import { useNavigate, useRouteLoaderData } from "@remix-run/react";
import { captureException, captureMessage } from "@sentry/remix";
import {
  AddressElement,
  CardElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  Stripe as StripeClient,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import { AlertCircle, Check } from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BookingCartSidebar from "~/components/booking/BookingCartSidebar";
import { InvoiceFields } from "~/components/common/InvoiceFields";
import LogoIcon from "~/components/icons/LogoIcon";
import { ScalapayWidget } from "~/components/scalapay/ScalapayWidget";
import { Button } from "~/components/ui/Button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  trackAddPaymentInfo,
  trackInitiateCheckout,
} from "~/lib/facebook-pixel";
import {
  mapBookingCartItemToGA4Item,
  trackGA4AddPaymentInfo,
  trackGA4BeginCheckout,
} from "~/lib/gtm-datalayer";
import {
  cn,
  getGiftCardBalance,
  getGiftCardCode,
  getGiftCardExpiryDate,
  isGiftCardUsable,
  maskGiftCardCode,
  sortGiftCardsByExpiry,
} from "~/lib/utils";
import { BookingCart } from "~/stores/bookingCart";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";
import { useUser } from "~/stores/user";

interface PaymentStepProps {
  onCancel: () => void;
  onConfirm: () => void;
  disableConfirm?: boolean;
  isSubmitting?: boolean;
  isVoucherBooking?: boolean;
  /**
   * Called immediately before this step hands the customer off to an external
   * payment provider (Scalapay's hosted checkout, or a Stripe 3DS challenge).
   * BookingPage uses it to disarm its own navigation guards: they exist to
   * catch a customer accidentally abandoning a half-filled booking, and an
   * intentional handoff is not that. See BookingPage's
   * `handleLeavingForPaymentProvider`.
   */
  onLeavingForPaymentProvider?: () => void;
}

// The "Richiedi fattura" option is hidden in the booking on client request.
// Everything behind it is kept: flip this back to true to show it again.
// While hidden, wantsInvoice stays false and every reservation is sent with
// invoiceRequested: false.
const SHOW_BOOKING_INVOICE_OPTION = false;

let stripePromise: Promise<StripeClient | null>;
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};
export default function PaymentStep({
  onCancel,
  onConfirm,
  disableConfirm = false,
  isSubmitting = false,
  isVoucherBooking = false,
  onLeavingForPaymentProvider,
}: PaymentStepProps) {
  const { t, i18n } = useTranslation();
  const { bookingCart } = useBookingCartStore();
  const rootData = useRouteLoaderData<any>("root");
  const [paymentMethod, setPaymentMethod] = useState<
    "card" | "pay_at_checkin" | "scalapay"
  >(
    isVoucherBooking ? "pay_at_checkin" : "card", //FIXME: Remove this once the voucher booking is implemented
  );
  const [paymentData, setPaymentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const paymentDataCalledRef = useRef(false);

  const summary = useMemo(() => {
    if (
      !bookingCart ||
      !bookingCart.BasketItems ||
      bookingCart.BasketItems.length === 0
    ) {
      return {
        total: 0,
        currency: "",
        netTotal: 0,
        discount: null,
        items: [],
      };
    }

    const total = bookingCart.BasketItems.reduce((acc, item) => {
      if (item.Role === "INCLUDED") {
        return acc;
      }
      return acc + (item.GrossAmount?.Price || 0);
    }, 0);

    const netTotal = bookingCart.TotalNetAmount?.Price || total;
    const discount = bookingCart.DiscountItems?.[0] || null;
    const currency = bookingCart.BasketItems[0].GrossAmount?.Currency || "";

    const mainServices = bookingCart.BasketItems.filter(
      (item) => item.Role !== "INCLUDED" && item.Role !== "INTEGRATION",
    );
    const upsellingServices = bookingCart.BasketItems.filter(
      (item) => item.Role === "INTEGRATION",
    );

    return {
      total,
      currency,
      netTotal,
      discount,
      mainServices,
      upsellingServices,
    };
  }, [bookingCart]);

  useEffect(() => {
    if (!paymentDataCalledRef.current) {
      paymentDataCalledRef.current = true;
      const fetchPaymentData = async () => {
        setLoading(true);
        try {
          const response = await fetch("/api/checkout/payment-data");
          const data: any = await response.json();

          if (data.error) {
            setError(data.error);
          } else {
            setPaymentData(data);
            if (data.publishableKey) {
              stripePromise = loadStripe(data.publishableKey);
            }
          }
        } catch (err) {
          console.error("Error fetching payment data:", err);
          setError("Failed to load payment data");
        } finally {
          setLoading(false);
        }
      };

      fetchPaymentData();
    }

    if (bookingCart && summary.total > 0) {
      try {
        const contentIds =
          bookingCart.BasketItems?.map(
            (item: any) => item.Service || item.LineNr?.toString() || "",
          ).filter(Boolean) || [];
        const contents =
          bookingCart.BasketItems?.map((item: any) => ({
            id: item.Service || item.LineNr?.toString() || "",
            quantity: item.Quantity?.Total || 1,
            item_price: item.GrossAmount?.Price || item.GrossPrice?.Price || 0,
          })) || [];

        trackInitiateCheckout({
          content_ids: contentIds,
          contents,
          currency: summary.currency || "EUR",
          value: summary.netTotal || summary.total,
          num_items: bookingCart.BasketItems?.length || 0,
        });

        const ga4Items = (bookingCart.BasketItems || []).map(
          mapBookingCartItemToGA4Item,
        );
        trackGA4BeginCheckout(
          ga4Items,
          summary.netTotal || summary.total,
          summary.currency || "EUR",
        );
      } catch (error) {
        console.error("Error tracking InitiateCheckout:", error);
      }
    }
  }, [bookingCart, summary]);

  const selectedPaymentMethodData = useMemo(() => {
    if (!paymentData?.bookingCart?.PaymentMethod) return null;

    const paymentMethods = paymentData.bookingCart.PaymentMethod;

    if (paymentMethod === "pay_at_checkin") {
      return paymentMethods.find((pm: any) => pm.Code === "PAY_AT_CHECKIN");
    } else {
      const payNowMethod = paymentMethods.find((pm: any) => pm.PayNow === true);
      return (
        payNowMethod ||
        paymentMethods.find((pm: any) => pm.Code === "FULL_ADVANCED_PAYMENT")
      );
    }
  }, [paymentMethod, paymentData?.bookingCart?.PaymentMethod]);

  const options: StripeElementsOptions | null = useMemo(
    () =>
      summary.netTotal > 0.5
        ? {
            appearance: { theme: "stripe" as const },
            mode: "payment",
            amount: Math.round(summary.netTotal * 100),
            currency: "eur",
            locale: i18n.language as any,
          }
        : null,
    [summary.netTotal, i18n.language],
  );

  if (error) {
    return (
      <div className="w-full text-center py-8">
        <h2 className="text-2xl font-semibold text-red-600 mb-4">
          {t("Errore di pagamento")}
        </h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button variant="outline" onClick={onCancel}>
          {t("Torna indietro")}
        </Button>
      </div>
    );
  }

  if (!loading && (!paymentData?.publishableKey || !stripePromise)) {
    return (
      <div className="w-full text-center py-8">
        <h2 className="text-2xl font-semibold mb-4">
          {t("Errore di configurazione")}
        </h2>
        <p className="text-gray-600 mb-6">
          {t("Impossibile configurare il pagamento. Riprova più tardi.")}
        </p>
        <Button variant="outline" onClick={onCancel}>
          {t("Torna indietro")}
        </Button>
      </div>
    );
  }

  const getCancellationPolicyText = (code: string) => {
    switch (code) {
      case "FULL_PENALTY":
        return t(
          "Non è previsto alcun rimborso in caso di cancellazione o mancata presentazione.",
        );
      case "PARTIAL_PENALTY":
        return t(
          "Accredito totale in caso di cancellazione entro la mezzanotte del giorno prima: verrà emessa una gift card dal valore totale della prenotazione, utilizzabile online o in cassa, con validità di 12 mesi. Nessun rimborso in caso di cancellazione il giorno del check-in o di no-show.",
        );
      case "NO_PENALTY":
        return t(
          "Nessuna penale in caso di cancellazione. In caso di no-show verrà addebitato il totale della prenotazione sulla carta di credito lasciata a garanzia.",
        );
      case "FLEXIBLE":
        return t("Flessibile");
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col px-4 lg:px-8 py-8">
      {!!options && (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">
            {t("Metodo di pagamento")}
          </h2>
          <p className="text-gray-600">
            {t("Scegli come vuoi pagare la tua prenotazione")}
          </p>
        </div>
      )}

      {loading && (
        <div className="w-full text-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
            <span>{t("Caricamento dati di pagamento...")}</span>
          </div>
        </div>
      )}
      {!loading && (
        <div className="w-full max-w-2xl mx-auto mt-6">
          <div className="space-y-6">
            <div className="lg:hidden mb-6">
              <BookingCartSidebar
                hideRemoveButtons={true}
                hideTitle={false}
                isVoucherBooking={isVoucherBooking}
                hasAddedToCartInThisFlow={true}
              />
            </div>

            {/*
              The Scalapay widget used to sit HERE, right under the mobile
              order summary, with a comment calling it "the closest near-the-
              total spot available". It has moved down to the Scalapay payment
              option (see below) for two reasons.

              The one Scalapay asked for: they want the dynamic widget at
              checkout next to the "Scalapay" method itself, which is also
              their own placement guideline.

              The one we found while moving it, and which matters more: up
              here it was gated on nothing but the root loader's
              SCALAPAY_MERCHANT_TOKEN, which app/root.tsx exposes whenever the
              SHOP gate `isScalapayEnabled` is on. The payment option below is
              gated on the BOOKING gate instead (`scalapayEnabled` from
              api.checkout.payment-data.ts = isScalapayBookingEnabled && not a
              voucher booking). Those two gates diverge in the DEFAULT
              configuration — .dev.vars.example documents SCALAPAY_BOOKING_ENABLED
              as off even when SCALAPAY_ENABLED is on — so this widget
              advertised "pay in instalments" on a page that then offered no
              way to pick Scalapay. Same for any voucher booking. Rendering it
              inside the gated block makes the promise and the option appear
              and disappear together, which is the only correct behaviour.
            */}

            {summary.netTotal < 0.5 &&
            bookingCart?.BasketItems?.length &&
            bookingCart.BasketItems.length > 0 ? (
              <div>
                <div className="flex flex-col items-center justify-center border border-green-500 p-4 rounded-lg bg-green-50 mb-6">
                  <h2 className="text-2xl font-semibold text-center text-green-600">
                    {t("Prenotazione gratuita")}
                  </h2>
                  <p className="text-green-600 text-center mt-2">
                    {t("La tua prenotazione è completamente gratuita.")}
                    <br />
                    {t(
                      "Non è richiesto alcun pagamento per confermare la prenotazione.",
                    )}
                  </p>
                </div>
                {bookingCart && (
                  <FreeOrderForm
                    bookingCart={bookingCart}
                    onCancel={onCancel}
                    onSuccess={onConfirm}
                    isVoucherBooking={isVoucherBooking}
                  />
                )}
              </div>
            ) : (
              <>
                {/* Stacked, full width, matching the shop checkout. Side by
                    side, the Scalapay option carries the promo widget under
                    it and pulled width away from the others until their
                    labels wrapped onto two lines and their check circles
                    flattened into ovals. */}
                <div className="flex flex-col gap-4">
                  <div
                    className={cn(
                      "bg-white shadow-md flex items-center justify-between px-4 py-3 cursor-pointer rounded-lg min-w-0",
                      {
                        "border-secondary border-2": paymentMethod === "card",
                      },
                    )}
                    onClick={() => {
                      setPaymentMethod("card");
                    }}
                  >
                    <span className="font-medium">{t("Paga adesso")}</span>
                    <div className="bg-gray-200 rounded-full w-6 h-6 shrink-0 flex items-center justify-center ml-6">
                      {paymentMethod === "card" ? (
                        <Check size={14} className="text-secondary" />
                      ) : null}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "bg-white shadow-md flex items-center justify-between px-4 py-3 cursor-pointer rounded-lg min-w-0",
                      {
                        "border-secondary border-2":
                          paymentMethod === "pay_at_checkin",
                      },
                    )}
                    onClick={() => {
                      setPaymentMethod("pay_at_checkin");
                    }}
                  >
                    <span className="font-medium">
                      {t("Paga in struttura")}
                    </span>
                    <div className="bg-gray-200 rounded-full w-6 h-6 shrink-0 flex items-center justify-center ml-6">
                      {paymentMethod === "pay_at_checkin" ? (
                        <Check size={14} className="text-secondary" />
                      ) : null}
                    </div>
                  </div>
                  {paymentData?.scalapayEnabled && paymentData?.scalapayEligible && (
                    <div className="flex flex-col gap-2">
                      <div
                        className={cn(
                          "bg-white shadow-md flex items-center justify-between px-4 py-3 cursor-pointer rounded-lg min-w-0",
                          {
                            "border-secondary border-2":
                              paymentMethod === "scalapay",
                          },
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setPaymentMethod("scalapay");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setPaymentMethod("scalapay");
                          }
                        }}
                      >
                        <span className="font-medium">
                          {t("Scalapay - Paga a Rate")}
                        </span>
                        <div className="bg-gray-200 rounded-full w-6 h-6 shrink-0 flex items-center justify-center ml-6">
                          {paymentMethod === "scalapay" ? (
                            <Check size={14} className="text-secondary" />
                          ) : null}
                        </div>
                      </div>
                      {/*
                        BELOW the option row, never inside it. The row is a
                        `role="button"`, and this widget carries its own
                        interactive part (the info modal Scalapay's script
                        wires up) — nesting it would put interactive content
                        inside a button and would also break the widget's own
                        rule against wrapping it in a button or using it as a
                        payment selector (see ScalapayWidget.tsx). Adjacent
                        satisfies "next to Scalapay" without either problem.
                      */}
                      <ScalapayWidget
                        merchantToken={rootData?.SCALAPAY_MERCHANT_TOKEN}
                        amount={summary.netTotal || summary.total}
                        minAmount={rootData?.SCALAPAY_MIN_AMOUNT ?? 0}
                        maxAmount={rootData?.SCALAPAY_MAX_AMOUNT ?? Infinity}
                        type="checkout"
                        channel="travel"
                        sandbox={rootData?.SCALAPAY_SANDBOX}
                        className="px-4"
                      />
                    </div>
                  )}
                </div>

                {selectedPaymentMethodData?.CancPolicy && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-800 mb-1">
                          {t("Politica di cancellazione")}
                        </h4>
                        <p className="text-sm text-amber-700">
                          {getCancellationPolicyText(
                            selectedPaymentMethodData.CancPolicy.Code,
                          ) || selectedPaymentMethodData.CancPolicy.Description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  {paymentMethod === "pay_at_checkin" && (
                    <>
                      <h3 className="text-xl font-semibold mb-2">
                        {t("Conferma prenotazione")}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4">
                        {t(
                          "Abbiamo bisogno di una garanzia per la prenotazione, inserisci i dati richiesti per confermare la prenotazione.",
                        )}
                      </p>
                    </>
                  )}
                  {paymentMethod === "card" && (
                    <>
                      <h3 className="text-xl font-semibold mb-2">
                        {t("Pagamento")}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4">
                        {t("Tutte le transazioni sono sicure e crittografate.")}
                      </p>
                    </>
                  )}
                  {paymentMethod === "scalapay" && (
                    <>
                      <h3 className="text-xl font-semibold mb-2">
                        {t("Paga con Scalapay")}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4">
                        {t(
                          "Verrai reindirizzato al sito di Scalapay per completare il pagamento in rate.",
                        )}
                      </p>
                    </>
                  )}

                  {!options && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-amber-800 mb-1">
                            {t("Pagamento non disponibile")}
                          </h4>

                          <Button variant="secondary" onClick={onCancel}>
                            {t("Torna indietro")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {options && (
                    <Elements options={options} stripe={stripePromise}>
                      {bookingCart && (
                        <CheckoutForm
                          onCancel={onCancel}
                          showOnlyCardInfo={paymentMethod === "pay_at_checkin"}
                          useScalapay={paymentMethod === "scalapay"}
                          bookingCart={bookingCart}
                          onSuccess={onConfirm}
                          isVoucherBooking={isVoucherBooking}
                          giftCardEnabled={
                            paymentData?.giftCardEnabled === true &&
                            !isVoucherBooking
                          }
                          onLeavingForPaymentProvider={
                            onLeavingForPaymentProvider
                          }
                        />
                      )}
                    </Elements>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FreeOrderForm({
  bookingCart,
  onSuccess,
  onCancel,
  isVoucherBooking = false,
}: {
  bookingCart: BookingCart;
  onSuccess: () => void;
  onCancel: () => void;
  isVoucherBooking?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bookingCart: currentBookingCart } = useBookingCartStore();
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [message, setMessage] = useState<string | null>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedCancellationPolicy, setAcceptedCancellationPolicy] =
    useState(false);
  const [acceptedSalesConditions, setAcceptedSalesConditions] =
    useState(false);
  const submittingRef = useRef(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;

    setMessage(null);
    setIsProcessing(true);

    if (!acceptedCancellationPolicy) {
      setMessage(
        t("Devi accettare le politiche di cancellazione per continuare"),
      );
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    if (!acceptedSalesConditions) {
      setMessage(
        t("Devi accettare le Condizioni Generali di Vendita per continuare"),
      );
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    if (!bookingCart?.ReferentData || !bookingCart?.GuestsData) {
      console.error("empty cart!");
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    setLoadingConfirm(true);

    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
      return null;
    };

    try {
      const createReservationResponse = await fetch(
        `/api/checkout/create-reservation?is_voucher_booking=${isVoucherBooking}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ReferentData: JSON.stringify(bookingCart.ReferentData),
            GuestsData: JSON.stringify(bookingCart.GuestsData),
            paymentMethodCode: "PAY_AT_CHECKIN",
          }),
        },
      );

      if (!createReservationResponse.ok) {
        setIsProcessing(false);
        setLoadingConfirm(false);
        submittingRef.current = false;
        setMessage(
          t("Si è verificato un errore con la creazione della prenotazione."),
        );
        return;
      }

      const createReservationData: any = await createReservationResponse.json();
      if (createReservationData.status !== "success") {
        setIsProcessing(false);
        setLoadingConfirm(false);
        submittingRef.current = false;
        setMessage(
          createReservationData.message ||
            t("Si è verificato un errore con la creazione della prenotazione."),
        );
        return;
      }

      const reservationCode = createReservationData.reservationCode;

      const clientSessionToken = getCookie("__client-session-token");
      const queryParams = new URLSearchParams();
      if (clientSessionToken) {
        queryParams.append("client_session_token", clientSessionToken);
      }
      if (isVoucherBooking) {
        queryParams.append("is_voucher_booking", "true");
      }
      queryParams.append("reservation_code", reservationCode);

      const url = `/api/checkout/confirmation${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reservationCode: reservationCode,
          vouchers: bookingCart.vouchers || [],
        }),
      })
        .then((res) => {
          if (!res.ok) {
            setLoadingConfirm(false);
            setIsProcessing(false);
            submittingRef.current = false;
            setMessage(
              t(
                "Si è verificato un errore con la creazione della prenotazione.",
              ),
            );
            return;
          }
          return res.json();
        })
        .then((data: any) => {
          if (!data) return;
          if (data?.status === "succeeded" && data?.reservationId) {
            if (
              data?.unmatchedVouchers &&
              data.unmatchedVouchers.length > 0 &&
              isVoucherBooking
            ) {
              alert(
                data.warning ||
                  `I seguenti voucher non sono stati utilizzati: ${data.unmatchedVouchers.join(
                    ", ",
                  )}`,
              );
            }

            if (
              currentBookingCart?.vouchers &&
              currentBookingCart.vouchers.length > 0
            ) {
              const basketItems = currentBookingCart.BasketItems || [];
              const cartServices =
                basketItems
                  .filter(
                    (item: any) =>
                      item.Role === "MAIN" || item.Role === "INTEGRATION",
                  )
                  .map((item: any) => item.Service) || [];

              const matchedVoucherCodes = new Set<string>();

              if (cartServices.length > 0) {
                cartServices.forEach((serviceCode: string) => {
                  const vouchersForService = (
                    currentBookingCart.vouchers || []
                  ).filter((v) => {
                    if (
                      !v.voucherData.ServicesIncludedCodes ||
                      v.voucherData.ServicesIncludedCodes.length === 0
                    ) {
                      return true;
                    }
                    return v.voucherData.ServicesIncludedCodes.includes(
                      serviceCode,
                    );
                  });

                  if (vouchersForService.length > 0) {
                    const voucherWithShortestExpiration =
                      vouchersForService.reduce((prev, curr) => {
                        const prevExp = prev.voucherData.ExpirationDate
                          ? new Date(prev.voucherData.ExpirationDate)
                          : null;
                        const currExp = curr.voucherData.ExpirationDate
                          ? new Date(curr.voucherData.ExpirationDate)
                          : null;

                        if (!prevExp) return curr;
                        if (!currExp) return prev;

                        return currExp < prevExp ? curr : prev;
                      });
                    matchedVoucherCodes.add(voucherWithShortestExpiration.code);
                  }
                });
              } else {
                const genericVouchers = (
                  currentBookingCart.vouchers || []
                ).filter(
                  (v) =>
                    !v.voucherData.ServicesIncludedCodes ||
                    v.voucherData.ServicesIncludedCodes.length === 0,
                );

                if (genericVouchers.length > 0) {
                  const shortestGeneric = genericVouchers.reduce(
                    (prev, curr) => {
                      const prevExp = prev.voucherData.ExpirationDate
                        ? new Date(prev.voucherData.ExpirationDate)
                        : null;
                      const currExp = curr.voucherData.ExpirationDate
                        ? new Date(curr.voucherData.ExpirationDate)
                        : null;

                      if (!prevExp) return curr;
                      if (!currExp) return prev;

                      return currExp < prevExp ? curr : prev;
                    },
                  );
                  matchedVoucherCodes.add(shortestGeneric.code);
                }
              }
            }

            navigate(`/order/confirmed?reservation-id=${data.reservationId}`);
          } else if (data?.status === "error") {
            setLoadingConfirm(false);
            setIsProcessing(false);
            submittingRef.current = false;
            setMessage(
              data?.message ||
                t(
                  "Si è verificato un errore con la creazione della prenotazione.",
                ),
            );
          } else {
            setLoadingConfirm(false);
            setIsProcessing(false);
            submittingRef.current = false;
            setMessage(
              t(
                "Si è verificato un errore con la creazione della prenotazione.",
              ),
            );
          }
        })
        .catch((error) => {
          console.error("Error confirming checkout:", error);
          setLoadingConfirm(false);
          setIsProcessing(false);
          submittingRef.current = false;
          setMessage(
            t("Si è verificato un errore con la creazione della prenotazione."),
          );
        });
    } catch (error) {
      console.error("Error creating reservation:", error);
      setIsProcessing(false);
      setLoadingConfirm(false);
      submittingRef.current = false;
      setMessage(
        t("Si è verificato un errore con la creazione della prenotazione."),
      );
    }
  };

  return (
    <>
      <form id="free-order-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="mt-4 border-t-[1px] border-b-[1px] border-dashed border-t-secondary border-b-secondary py-4 space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="accept-cancellation-policy-free"
              checked={acceptedCancellationPolicy}
              onCheckedChange={(checked) => {
                setAcceptedCancellationPolicy(checked === true);
              }}
              className="data-[state=checked]:bg-secondary data-[state=checked]:text-white rounded-none"
            />
            <label
              htmlFor="accept-cancellation-policy-free"
              className="text-lg font-bold cursor-pointer"
            >
              {t("Ho letto e accetto i termini del")}{" "}
              <a
                href="/spa-regulations"
                target="_blank"
                className="text-secondary underline hover:text-secondary/80"
              >
                {t("Regolamento ChorusLife Immersive SPA")}
              </a>
              {" *"}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="accept-sales-conditions-free"
              checked={acceptedSalesConditions}
              onCheckedChange={(checked) => {
                setAcceptedSalesConditions(checked === true);
              }}
              className="data-[state=checked]:bg-secondary data-[state=checked]:text-white rounded-none"
            />
            <label
              htmlFor="accept-sales-conditions-free"
              className="text-lg font-bold cursor-pointer"
            >
              {t("Accetto le")}{" "}
              <a
                href="/terms-and-conditions"
                target="_blank"
                className="text-secondary underline hover:text-secondary/80"
              >
                {t("Condizioni Generali di Vendita")}
              </a>{" "}
              {t("di ChorusWellness S.r.l.")}
              {" *"}
            </label>
          </div>
        </div>

        <div className="flex flex-row items-center justify-center space-x-2">
          <Button
            variant={"muted"}
            onClick={() => {
              onCancel();
            }}
          >
            <span>{t("Indietro")}</span>
          </Button>

          <Button
            type="submit"
            variant="secondary"
            disabled={
              isProcessing ||
              !acceptedCancellationPolicy ||
              !acceptedSalesConditions
            }
            className="py-3 px-10 font-semibold disabled:opacity-50"
          >
            <span id="button-text">
              {isProcessing
                ? t("Conferma in corso") + "..."
                : t("Conferma prenotazione")}
            </span>
          </Button>
        </div>

        {message && (
          <div
            id="payment-message"
            className="text-red-500 text-sm mt-2 text-center w-full block"
          >
            {message}
          </div>
        )}
      </form>

      {loadingConfirm && (
        <div className="fixed top-0 left-0 w-full h-full inset-0 z-50 space-y-4 flex flex-col items-center justify-center bg-white bg-opacity-75 backdrop-blur-sm animate-in fade-in-60">
          <span className="text-xl block mb-4 animate-pulse">
            {t("Sto creando la prenotazione")}...
          </span>
          <LogoIcon className="fill-secondary -ml-4" />
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        </div>
      )}
    </>
  );
}


// Booking_AddAdvancedPayment refusals that deserve their own explanation
// (ClickUp CHLSPA-003). Keys are the Italian source strings used by
// i18next; any other code keeps the generic "Gift card non accettata".
const GIFT_CARD_ERROR_MESSAGES: Record<number, string> = {
  651: "Non c'è nessun importo residuo da pagare su questa prenotazione.",
  655: "Gift card non valida: il codice inserito non esiste.",
  656: "La gift card è scaduta.",
  657: "La gift card non ha credito residuo.",
  658: "La gift card è bloccata, attendi 15 minuti e riprova.",
};

function CheckoutForm({
  bookingCart,
  showOnlyCardInfo,
  useScalapay = false,
  onSuccess,
  onCancel,
  isVoucherBooking = false,
  giftCardEnabled = false,
  onLeavingForPaymentProvider,
}: {
  bookingCart: BookingCart;
  showOnlyCardInfo: boolean;
  useScalapay?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
  isVoucherBooking?: boolean;
  giftCardEnabled?: boolean;
  onLeavingForPaymentProvider?: () => void;
}): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [message, setMessage] = useState<string | null>();
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    setBookingCart,
    bookingCart: currentBookingCart,
    setAppliedGiftCards,
    clearAppliedGiftCards,
  } = useBookingCartStore();
  const user = useUser();
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [acceptedCancellationPolicy, setAcceptedCancellationPolicy] =
    useState(false);
  const [acceptedSalesConditions, setAcceptedSalesConditions] =
    useState(false);
  const isRedirectingRef = useRef(false);
  const reservationCodeRef = useRef<string | null>(null);
  const allowNavigationRef = useRef(false);
  const submittingRef = useRef(false);
  const beforeUnloadHandlerRef = useRef<
    ((e: BeforeUnloadEvent) => void) | null
  >(null);

  // Gift card advanced payments: the cards are charged as soon as they are
  // applied (Booking_AddAdvancedPayment), so the reservation is created at the
  // first "apply" and reused at submit. The amounts shown here are echoes of
  // the server state kept in the signed session cookie.
  const [giftCards, setGiftCards] = useState<
    Array<{
      id: string;
      transactionId: string;
      cardCode: string;
      amount: number;
      currency: string;
    }>
  >([]);
  const [giftRemaining, setGiftRemaining] = useState<number | null>(null);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  // Remaining balance left ON each card after its own charge (per-card, not
  // an aggregate): keyed by the applied transaction's `id`, same key as
  // `appliedRawCodeById`. Populated on a successful apply, pruned on remove
  // and on release - never blanket-cleared on every apply/remove, otherwise
  // an already-applied card would lose its residual line when a second card
  // is applied.
  const [giftCardBalances, setGiftCardBalances] = useState<
    Record<string, { amount: number; currency: string }>
  >({});

  // Gift card picker: offers the logged-in customer's own gift cards
  // (api.gift-cards.ts, the same endpoint the account tab uses) instead of
  // making them retype the code. Best-effort only - a failed/empty fetch
  // must leave the manual input working exactly as before, so failures are
  // swallowed and simply leave the list empty.
  const [ownGiftCards, setOwnGiftCards] = useState<any[]>([]);
  useEffect(() => {
    if (!giftCardEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/gift-cards");
        if (!res.ok) return;
        const data: any = await res.json();
        if (!cancelled && Array.isArray(data?.giftCards)) {
          setOwnGiftCards(data.giftCards);
        }
      } catch (error) {
        console.error("Error fetching customer's gift cards:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [giftCardEnabled]);

  // Raw (unmasked) code each currently-applied transaction was applied with,
  // keyed by transaction id: the only way to tell whether one of the
  // customer's own cards is "already applied to this reservation" and must
  // be excluded from the picker, since the cards mirrored in `giftCards`
  // have their code masked by the server before it ever reaches the client.
  const [appliedRawCodeById, setAppliedRawCodeById] = useState<
    Record<string, string>
  >({});

  const selectableOwnGiftCards = useMemo(() => {
    const appliedRawCodes = new Set(Object.values(appliedRawCodeById));
    // Soonest-expiring first, so the customer spends the card closest to
    // expiry before it becomes unusable.
    return sortGiftCardsByExpiry(ownGiftCards.filter(isGiftCardUsable), "asc")
      .map((card) => ({
        code: getGiftCardCode(card),
        balance: getGiftCardBalance(card),
        expiryDate: getGiftCardExpiryDate(card),
      }))
      .filter((card) => card.code && !appliedRawCodes.has(card.code));
  }, [ownGiftCards, appliedRawCodeById]);

  const fullyCoveredByGift = giftCards.length > 0 && giftRemaining === 0;
  // A residual below Stripe's minimum chargeable amount cannot be paid by
  // card nor confirmed as fully covered: the user must remove a gift card.
  const giftRemainingBelowMinimum =
    giftCards.length > 0 &&
    giftRemaining !== null &&
    giftRemaining > 0 &&
    giftRemaining < 0.5;

  // Mirrors for the unmount/pagehide release handlers (state is stale in
  // closures registered once).
  const giftCardsRef = useRef<typeof giftCards>([]);
  useEffect(() => {
    giftCardsRef.current = giftCards;
  }, [giftCards]);

  // Mirrors the currently applied gift cards into the booking cart store so
  // the sidebar (which reads the cart from the store, not from this
  // component) can render them. `cardCode` here is already masked by
  // api.checkout.gift-card.tsx, so it is safe to keep in the persisted
  // store. Called from every place that changes `giftCards` (apply, remove,
  // release), so the sidebar never sees a state this component itself
  // doesn't have.
  const syncAppliedGiftCards = (
    cards: typeof giftCards,
    remaining: number | null,
  ) => {
    setAppliedGiftCards(
      cards.map((card) => ({
        id: card.id,
        maskedCardCode: card.cardCode,
        amount: card.amount,
        currency: card.currency,
      })),
      remaining,
    );
  };
  // Set right before navigating to the confirmed page: the applied gift
  // transactions belong to the confirmed reservation and must NOT be refunded
  // by the abandon handlers.
  const giftCommittedRef = useRef(false);
  // Tracks a releaseGiftCards() call already in flight so a second trigger
  // (e.g. Submit racing the showOnlyCardInfo/useScalapay effect below, or a
  // fast double-click) awaits the SAME request instead of firing a duplicate
  // delete against Axalta for the same reservation.
  const giftCardReleaseInFlightRef = useRef<Promise<boolean> | null>(null);

  const cartNetTotal = () => {
    const total = (bookingCart.BasketItems || []).reduce(
      (acc: number, item: any) => {
        if (item.Role === "INCLUDED") return acc;
        return acc + (item.GrossAmount?.Price || 0);
      },
      0,
    );
    return bookingCart.TotalNetAmount?.Price || total;
  };

  const cartCurrency = () =>
    bookingCart.BasketItems?.[0]?.GrossAmount?.Currency || "EUR";

  const formatAmount = (value: number, currency?: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: currency || cartCurrency(),
    }).format(value);

  // Same calendar-date-in-UTC formatting as GiftCardGridItem's own
  // `formatDate`, used only for the short expiry hint in the gift card
  // picker below.
  const formatGiftCardExpiry = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  // Keeps the deferred Elements amount in sync with what Stripe will actually
  // charge: confirmPayment rejects a client secret whose amount differs from
  // the Elements one.
  const updateStripeAmount = (amount: number | null) => {
    if (!elements || amount === null || amount < 0.5) return;
    try {
      elements.update({ amount: Math.round(amount * 100) });
    } catch (err) {
      console.error("Error updating Stripe elements amount:", err);
    }
  };

  // Creates the FULL_ADVANCED_PAYMENT reservation if not created yet: gift
  // cards can only be charged against an existing reservation.
  const ensureReservation = async (): Promise<string | null> => {
    if (reservationCodeRef.current) return reservationCodeRef.current;

    let billingFirstName = "";
    let billingLastName = "";
    // Billing names only matter when an invoice is requested. The read is
    // raced against a timeout because the Stripe iframe may never answer
    // (e.g. when it failed to initialize) and must not block the gift card.
    if (wantsInvoice) {
      try {
        const addressElement = elements?.getElement("address");
        if (addressElement) {
          const addressValue = await Promise.race([
            addressElement.getValue(),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
          ]);
          if (addressValue?.complete) {
            billingFirstName = addressValue.value.firstName || "";
            billingLastName = addressValue.value.lastName || "";
          }
        }
      } catch {
        // An incomplete address must not block the gift card application.
      }
    }

    const createReservationResponse = await fetch(
      `/api/checkout/create-reservation?is_voucher_booking=${isVoucherBooking}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ReferentData: JSON.stringify(bookingCart.ReferentData),
          GuestsData: JSON.stringify(bookingCart.GuestsData),
          invoiceRequested: wantsInvoice,
          billingFirstName,
          billingLastName,
          vouchers: bookingCart.vouchers || [],
          basketItems: bookingCart.BasketItems || [],
          paymentMethodCode: "FULL_ADVANCED_PAYMENT",
        }),
      },
    );

    if (!createReservationResponse.ok) return null;
    const createReservationData: any = await createReservationResponse.json();
    if (createReservationData.status !== "success") return null;

    reservationCodeRef.current = createReservationData.reservationCode;
    return reservationCodeRef.current;
  };

  const handleApplyGiftCard = async () => {
    const code = giftCardCode.trim();
    if (!code || giftCardLoading || isProcessing) return;

    setGiftCardError(null);

    if (!bookingCart?.ReferentData || !bookingCart?.GuestsData) {
      setGiftCardError(t("Carrello vuoto. Torna alla prenotazione."));
      return;
    }

    setGiftCardLoading(true);
    try {
      const reservationCode = await ensureReservation();
      if (!reservationCode) {
        setGiftCardError(
          t("Si è verificato un errore con la creazione della prenotazione."),
        );
        return;
      }

      const response = await fetch(
        `/api/checkout/gift-card?is_voucher_booking=${isVoucherBooking}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            intent: "apply",
            reservationCode,
            cardCode: code,
          }),
        },
      );
      const data: any = await response.json();

      if (!response.ok || data?.status !== "success") {
        const knownMessage = GIFT_CARD_ERROR_MESSAGES[Number(data?.code)];
        setGiftCardError(
          knownMessage
            ? t(knownMessage)
            : data?.message
              ? `${t("Gift card non accettata")}: ${data.message}`
              : t("Gift card non accettata"),
        );
        return;
      }

      const appliedCards = data.giftCards || [];
      const appliedRemaining =
        typeof data.remaining === "number" ? data.remaining : null;
      setGiftCards(appliedCards);
      setGiftRemaining(appliedRemaining);
      syncAppliedGiftCards(appliedCards, appliedRemaining);
      // Remember the raw code this transaction was applied with (the server
      // only ever echoes it back masked): needed to keep the picker above
      // from re-offering a card that is already applied.
      const previouslyAppliedIds = new Set(giftCards.map((g) => g.id));
      const newlyApplied = appliedCards.find(
        (g: any) => !previouslyAppliedIds.has(g.id),
      );
      if (newlyApplied) {
        setAppliedRawCodeById((prev) => ({ ...prev, [newlyApplied.id]: code }));
        if (typeof data.balanceAmount === "number") {
          setGiftCardBalances((prev) => ({
            ...prev,
            [newlyApplied.id]: {
              amount: data.balanceAmount,
              currency: data.balanceCurrency || "EUR",
            },
          }));
        }
      }
      setGiftCardCode("");
      updateStripeAmount(data.remaining);
    } catch (error) {
      console.error("Error applying gift card:", error);
      setGiftCardError(
        t("Si è verificato un errore con la gift card. Riprova."),
      );
    } finally {
      setGiftCardLoading(false);
    }
  };

  const handleRemoveGiftCard = async (id: string) => {
    if (giftCardLoading || isProcessing || !reservationCodeRef.current) return;

    setGiftCardError(null);
    setGiftCardLoading(true);
    try {
      const response = await fetch(
        `/api/checkout/gift-card?is_voucher_booking=${isVoucherBooking}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            intent: "remove",
            reservationCode: reservationCodeRef.current,
            transactionId: id,
          }),
        },
      );
      const data: any = await response.json();

      if (!response.ok || data?.status !== "success") {
        setGiftCardError(
          data?.message
            ? `${t("Impossibile rimuovere la gift card")}: ${data.message}`
            : t("Impossibile rimuovere la gift card"),
        );
        return;
      }

      const updated = data.giftCards || [];
      setGiftCards(updated);
      setAppliedRawCodeById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      // Drop the removed card's residual and prune any id no longer present
      // in the server's updated list, so the map never drifts out of sync
      // with `giftCards`.
      setGiftCardBalances((prev) => {
        const stillApplied = new Set(updated.map((g: any) => g.id));
        const next: typeof prev = {};
        for (const [cardId, balance] of Object.entries(prev)) {
          if (cardId !== id && stillApplied.has(cardId)) {
            next[cardId] = balance;
          }
        }
        return next;
      });
      if (updated.length === 0) {
        setGiftRemaining(null);
        clearAppliedGiftCards();
        // The reservation was created with the invoice/billing data current
        // at the first apply: with no gift charges left on it, drop it so
        // the next apply/submit creates a fresh one with up-to-date data.
        reservationCodeRef.current = null;
        updateStripeAmount(cartNetTotal());
      } else {
        const remainingAfterRemove =
          typeof data.remaining === "number" ? data.remaining : null;
        setGiftRemaining(remainingAfterRemove);
        syncAppliedGiftCards(updated, remainingAfterRemove);
        updateStripeAmount(data.remaining);
      }
    } catch (error) {
      console.error("Error removing gift card:", error);
      setGiftCardError(
        t("Si è verificato un errore con la gift card. Riprova."),
      );
    } finally {
      setGiftCardLoading(false);
    }
  };

  // Best-effort refund of every applied gift card (user went back or switched
  // payment method). The reservation created for the gift flow is abandoned
  // and a fresh one will be created on the next submit.
  // Returns whether the release request was confirmed by the server: false
  // means the reservation may still hold a debited gift card, so a caller
  // that must not proceed with money at risk (the Scalapay submit path)
  // can refuse to continue and let the user retry.
  const releaseGiftCards = async (): Promise<boolean> => {
    // De-dupe: a second caller (e.g. Submit racing the effect below) awaits
    // the same in-flight attempt instead of firing another delete for the
    // same reservation.
    if (giftCardReleaseInFlightRef.current) {
      return giftCardReleaseInFlightRef.current;
    }
    if (giftCards.length === 0 || !reservationCodeRef.current) return true;

    const attempt = (async (): Promise<boolean> => {
      setGiftCardLoading(true);
      let released = false;
      try {
        const response = await fetch(
          `/api/checkout/gift-card?is_voucher_booking=${isVoucherBooking}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              intent: "release",
              reservationCode: reservationCodeRef.current,
            }),
            // Defence in depth: a hard redirect (Scalapay, 3DS) can follow
            // right after this call - keepalive keeps it in flight instead
            // of letting the navigation cut it short, matching the unmount
            // release fetch below.
            keepalive: true,
          },
        );
        const data: any = await response.json().catch(() => null);
        released =
          response.ok &&
          data?.status === "success" &&
          // The route can respond 200/"success" while one or more gift
          // cards actually failed to release server-side (Axalta refused
          // the refund) - see api.checkout.gift-card.tsx. Treat that as a
          // failure too: otherwise this check is decorative for exactly
          // the case where money is stuck on the abandoned reservation.
          (!data?.release || data.release.failed === 0);
      } catch (error) {
        console.error("Error releasing gift cards:", error);
      } finally {
        setGiftCardLoading(false);
      }

      if (released) {
        reservationCodeRef.current = null;
        setGiftCards([]);
        setGiftRemaining(null);
        setGiftCardBalances({});
        setGiftCardError(null);
        setAppliedRawCodeById({});
        clearAppliedGiftCards();
        updateStripeAmount(cartNetTotal());
      } else {
        // Keep the reservation handle and the applied gift cards in state:
        // clearing them here would make a caller believe the debited
        // reservation was abandoned safely when it was not, and would rob
        // the unmount/pagehide release guards of their one remaining chance
        // to retry it.
        setGiftCardError(
          t("Impossibile rilasciare la gift card applicata. Riprova."),
        );
      }
      return released;
    })();

    giftCardReleaseInFlightRef.current = attempt;
    try {
      return await attempt;
    } finally {
      giftCardReleaseInFlightRef.current = null;
    }
  };

  useEffect(() => {
    // Switching to "pay at check-in" OR to Scalapay abandons the
    // FULL_ADVANCED_PAYMENT reservation: any gift card already charged on it
    // must be refunded. Scalapay finances the full amount itself (see
    // app/lib/scalapay-booking.server.ts) - mixing it with gift cards, which
    // are charged immediately and reduce the amount owed, is not supported
    // and would need a product decision this feature does not have.
    // giftCards.length is a dependency so the release also runs when an
    // in-flight apply resolves AFTER the user already switched tab.
    if ((showOnlyCardInfo || useScalapay) && giftCards.length > 0) {
      releaseGiftCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyCardInfo, useScalapay, giftCards.length]);

  useEffect(() => {
    // Abandoning the checkout via client-side navigation (browser back, nav
    // links) unmounts this form without passing through the "Indietro"
    // button: refund any charged gift card best-effort on the way out.
    return () => {
      if (giftCommittedRef.current) return;
      const code = reservationCodeRef.current;
      if (giftCardsRef.current.length === 0 || !code) return;
      try {
        fetch(`/api/checkout/gift-card?is_voucher_booking=${isVoucherBooking}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "release", reservationCode: code }),
          keepalive: true,
        });
      } catch (error) {
        console.error("Error releasing gift cards on unmount:", error);
      }
      // Fire-and-forget: unlike releaseGiftCards() above, there is no
      // response to check here (the component is already gone by the time
      // it would arrive). Clear the store's copy anyway so the sidebar
      // doesn't keep showing a gift card applied to a reservation this
      // component just abandoned.
      clearAppliedGiftCards();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Tab close / reload with charged gift cards: refund via beacon.
    // Skipped while a Stripe redirect (3DS) or a confirm is in flight
    // (isRedirectingRef) and after a successful confirm (giftCommittedRef).
    if (giftCards.length === 0) return;
    const handler = () => {
      if (giftCommittedRef.current || isRedirectingRef.current) return;
      const code = reservationCodeRef.current;
      if (!code) return;
      try {
        navigator.sendBeacon(
          `/api/checkout/gift-card?is_voucher_booking=${isVoucherBooking}`,
          new Blob(
            [JSON.stringify({ intent: "release", reservationCode: code })],
            { type: "application/json" },
          ),
        );
      } catch (error) {
        console.error("Error sending gift card release beacon:", error);
      }
      // Clear the sidebar's persisted copy in the SAME branch that sends the
      // release. The unmount cleanup above already does this for a client-side
      // navigation, but a reload or a tab close never unmounts cleanly, so
      // without this the cards stay in localStorage and the sidebar comes back
      // after the reload claiming an advance payment that this very handler
      // just refunded. Deliberately inside the guard: when the confirm has
      // committed or a redirect is in flight we do NOT release, so the cards
      // are genuinely still applied and the sidebar must keep showing them.
      // If the beacon never arrives the sidebar under-reports instead of
      // over-reporting, which is the safer of the two directions: better to
      // omit an advance payment than to tell a customer money is already
      // covered when it may not be.
      clearAppliedGiftCards();
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftCards.length, isVoucherBooking]);

  useEffect(() => {
    return () => {
      allowNavigationRef.current = false;
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;

    setMessage(null);
    setIsProcessing(true);

    if (!acceptedCancellationPolicy) {
      setMessage(
        t("Devi accettare le politiche di cancellazione per continuare"),
      );
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    if (!acceptedSalesConditions) {
      setMessage(
        t("Devi accettare le Condizioni Generali di Vendita per continuare"),
      );
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    // Stripe is not involved when the advance payment is fully covered by
    // gift cards, nor when Scalapay was chosen (Scalapay finances the full
    // amount itself - see app/lib/scalapay-booking.server.ts): don't block
    // the confirm on Stripe's readiness in either case.
    if (!fullyCoveredByGift && !useScalapay && (!stripe || !elements)) {
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    let finalCountry = "";
    let billingFirstName = "";
    let billingLastName = "";
    // Only read by the Scalapay branch below, to build the `shipping`/
    // `billing` address Scalapay's schema requires (see
    // app/routes/api.checkout.scalapay.create.tsx) - harmless to collect
    // unconditionally since the other branches simply ignore them.
    let billingLine1 = "";
    let billingCity = "";
    let billingPostalCode = "";

    try {
      const addressElement = elements?.getElement("address");
      if (addressElement) {
        // Raced against a timeout: if the Stripe iframe failed to initialize
        // it may never answer, and the submit must not hang forever.
        const addressValue = await Promise.race([
          addressElement.getValue(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
        ]);
        if (addressValue?.complete) {
          if (addressValue.value.address?.country) {
            finalCountry = addressValue.value.address.country;
          }
          if (addressValue.value.address?.line1) {
            billingLine1 = addressValue.value.address.line1;
          }
          if (addressValue.value.address?.city) {
            billingCity = addressValue.value.address.city;
          }
          if (addressValue.value.address?.postal_code) {
            billingPostalCode = addressValue.value.address.postal_code;
          }
          if (addressValue.value.firstName) {
            billingFirstName = addressValue.value.firstName;
          }
          if (addressValue.value.lastName) {
            billingLastName = addressValue.value.lastName;
          }
        }
      }
    } catch (err) {
      setIsProcessing(false);
      setLoadingConfirm(false);
      submittingRef.current = false;
      setMessage(
        t("Si è verificato un errore con la creazione della prenotazione."),
      );
      console.error("Error getting address from AddressElement:", err);
    }

    if (!bookingCart?.ReferentData || !bookingCart?.GuestsData) {
      console.error("empty cart!");
      captureMessage("Empty cart in payment step", {
        level: "error",
        tags: {
          event: "empty_cart",
        },
        extra: {
          cart: bookingCart,
          hasReferentData: !!bookingCart?.ReferentData,
          hasGuestsData: !!bookingCart?.GuestsData,
        },
      });
      setMessage(t("Carrello vuoto. Torna alla prenotazione."));
      setIsProcessing(false);
      submittingRef.current = false;
      return;
    }

    // Clear any invoice data from cart
    bookingCart.customTaxCode = undefined;
    bookingCart.customVatId = undefined;
    bookingCart.customTaxCodeOrVAT = undefined;
    bookingCart.customCustomerType = undefined;
    bookingCart.customCompanyName = undefined;
    bookingCart.customLegalEmailAddress = undefined;
    bookingCart.customInterchangeId = undefined;
    setBookingCart(bookingCart);

    try {
      if (showOnlyCardInfo) {
        if (!stripe || !elements) {
          setIsProcessing(false);
          submittingRef.current = false;
          return;
        }
        const cardElement = elements.getElement(CardElement);
        if (cardElement) {
          const { token, error } = await stripe.createToken(cardElement);
          if (error) {
            setIsProcessing(false);
            setLoadingConfirm(false);
            submittingRef.current = false;
            setMessage(
              error.message ||
                t("Errore nella creazione del token di pagamento."),
            );
            return;
          }
          if (token && token.id && token.card) {
            setLoadingConfirm(true);

            const getCookie = (name: string): string | null => {
              const value = `; ${document.cookie}`;
              const parts = value.split(`; ${name}=`);
              if (parts.length === 2)
                return parts.pop()?.split(";").shift() || null;
              return null;
            };

            const createReservationResponse = await fetch(
              `/api/checkout/create-reservation?is_voucher_booking=${isVoucherBooking}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  ReferentData: JSON.stringify(bookingCart.ReferentData),
                  GuestsData: JSON.stringify(bookingCart.GuestsData),
                  invoiceRequested: wantsInvoice,
                  paymentMethodCode: "PAY_AT_CHECKIN",
                  PaymentToken: token.id,
                }),
              },
            );

            if (!createReservationResponse.ok) {
              setIsProcessing(false);
              setLoadingConfirm(false);
              submittingRef.current = false;
              setMessage(
                t(
                  "Si è verificato un errore con la creazione della prenotazione.",
                ),
              );
              return;
            }

            const createReservationData: any =
              await createReservationResponse.json();
            if (createReservationData.status !== "success") {
              setIsProcessing(false);
              setLoadingConfirm(false);
              submittingRef.current = false;
              setMessage(
                createReservationData.message ||
                  t(
                    "Si è verificato un errore con la creazione della prenotazione.",
                  ),
              );
              return;
            }

            const reservationCode = createReservationData.reservationCode;

            const clientSessionToken = getCookie("__client-session-token");
            const queryParams = new URLSearchParams();
            if (clientSessionToken) {
              queryParams.append("client_session_token", clientSessionToken);
            }
            if (isVoucherBooking) {
              queryParams.append("is_voucher_booking", "true");
            }
            queryParams.append("reservation_code", reservationCode);

            const url = `/api/checkout/confirmation${
              queryParams.toString() ? `?${queryParams.toString()}` : ""
            }`;

            fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                reservationCode: reservationCode,
                PaymentToken: token.id,
                CardInfo: {
                  fingerprint: token.card?.id || null,
                  brand: token.card?.brand,
                  exp_month: token.card?.exp_month,
                  exp_year: token.card?.exp_year,
                  last4: token.card?.last4,
                  name: token.card?.name,
                },
              }),
            })
              .then((res) => {
                if (!res.ok) {
                  setLoadingConfirm(false);
                  setIsProcessing(false);
                  submittingRef.current = false;
                  setMessage(
                    t(
                      "Si è verificato un errore con la creazione della prenotazione.",
                    ),
                  );
                  return;
                }
                return res.json();
              })
              .then((data: any) => {
                if (!data) return;
                if (data?.status === "succeeded" && data?.reservationId) {
                  // Mostra alert se ci sono voucher non utilizzati
                  if (
                    isVoucherBooking &&
                    data?.unmatchedVouchers &&
                    data.unmatchedVouchers.length > 0
                  ) {
                    alert(
                      data.warning ||
                        `I seguenti voucher non sono stati utilizzati: ${data.unmatchedVouchers.join(
                          ", ",
                        )}`,
                    );
                  }

                  // Rimuovi i voucher utilizzati prima di cancellare il carrello
                  if (
                    currentBookingCart?.vouchers &&
                    currentBookingCart.vouchers.length > 0
                  ) {
                    const basketItems = currentBookingCart.BasketItems || [];
                    const cartServices =
                      basketItems
                        .filter(
                          (item: any) =>
                            item.Role === "MAIN" || item.Role === "INTEGRATION",
                        )
                        .map((item: any) => item.Service) || [];

                    const matchedVoucherCodes = new Set<string>();
                    const vouchers = currentBookingCart.vouchers || [];

                    if (cartServices.length > 0) {
                      cartServices.forEach((serviceCode: string) => {
                        const vouchersForService = vouchers.filter((v) => {
                          if (
                            !v.voucherData.ServicesIncludedCodes ||
                            v.voucherData.ServicesIncludedCodes.length === 0
                          ) {
                            return true;
                          }
                          return v.voucherData.ServicesIncludedCodes.includes(
                            serviceCode,
                          );
                        });

                        if (vouchersForService.length > 0) {
                          const voucherWithShortestExpiration =
                            vouchersForService.reduce((prev, curr) => {
                              const prevExp = prev.voucherData.ExpirationDate
                                ? new Date(prev.voucherData.ExpirationDate)
                                : null;
                              const currExp = curr.voucherData.ExpirationDate
                                ? new Date(curr.voucherData.ExpirationDate)
                                : null;

                              if (!prevExp) return curr;
                              if (!currExp) return prev;

                              return currExp < prevExp ? curr : prev;
                            });
                          matchedVoucherCodes.add(
                            voucherWithShortestExpiration.code,
                          );
                        }
                      });
                    } else {
                      const genericVouchers = vouchers.filter(
                        (v) =>
                          !v.voucherData.ServicesIncludedCodes ||
                          v.voucherData.ServicesIncludedCodes.length === 0,
                      );

                      if (genericVouchers.length > 0) {
                        const shortestGeneric = genericVouchers.reduce(
                          (prev, curr) => {
                            const prevExp = prev.voucherData.ExpirationDate
                              ? new Date(prev.voucherData.ExpirationDate)
                              : null;
                            const currExp = curr.voucherData.ExpirationDate
                              ? new Date(curr.voucherData.ExpirationDate)
                              : null;

                            if (!prevExp) return curr;
                            if (!currExp) return prev;

                            return currExp < prevExp ? curr : prev;
                          },
                        );
                        matchedVoucherCodes.add(shortestGeneric.code);
                      }
                    }
                  }

                  navigate(
                    `/order/confirmed?reservation-id=${data.reservationId}`,
                  );
                } else if (data?.status === "error") {
                  setLoadingConfirm(false);
                  setIsProcessing(false);
                  submittingRef.current = false;
                  setMessage(
                    data?.message ||
                      t(
                        "Si è verificato un errore con la creazione della prenotazione.",
                      ),
                  );
                } else {
                  // Caso in cui la risposta non è valida
                  setLoadingConfirm(false);
                  setIsProcessing(false);
                  submittingRef.current = false;
                  setMessage(
                    t(
                      "Si è verificato un errore con la creazione della prenotazione.",
                    ),
                  );
                }
              })
              .catch((error) => {
                console.error("Error confirming checkout:", error);
                setLoadingConfirm(false);
                setIsProcessing(false);
                submittingRef.current = false;
                setMessage(
                  t(
                    "Si è verificato un errore con la creazione della prenotazione.",
                  ),
                );
              });
          }
        }
      } else if (useScalapay) {
        // Scalapay: a single request creates BOTH the Axalta reservation and
        // the Scalapay order server-side (see
        // app/routes/api.checkout.scalapay.create.tsx) - unlike the other
        // two branches, the reservation is not created here first. The
        // customer is then sent off-site with a hard redirect; the return
        // trip (app/routes/api.checkout.scalapay.return.tsx) verifies
        // payment and confirms the reservation before landing on the
        // existing /order/confirmed page.

        // Money safety: Scalapay finances the full amount itself and always
        // creates a brand-new reservation below, overwriting
        // `reservationCodeRef`. If a gift card is still applied - or a
        // release from the showOnlyCardInfo/useScalapay effect above is
        // already in flight - that ref is the only handle on the OLD
        // reservation the gift card was debited against; losing it before
        // the refund is confirmed would strand that charge forever. Drive
        // the release to completion (a no-op if there is nothing to
        // release) and refuse to proceed on failure so the user can retry
        // instead of silently walking into Scalapay with money still on the
        // abandoned reservation.
        const giftCardsReleased = await releaseGiftCards();
        if (!giftCardsReleased) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          setMessage(
            t("Impossibile rilasciare la gift card applicata. Riprova."),
          );
          return;
        }

        // Same AddPaymentInfo tracking as the card branch above, at the same
        // logical point (on submit, before any network call/redirect). The
        // `submittingRef` re-entrancy guard at the top of this handler is
        // the only guard the card branch relies on too, so this call is
        // protected the same way against a double-click or a rapid resubmit.
        if (
          bookingCart &&
          bookingCart.BasketItems &&
          bookingCart.BasketItems.length > 0
        ) {
          const total = bookingCart.BasketItems.reduce(
            (acc: number, item: any) => {
              if (item.Role === "INCLUDED") {
                return acc;
              }
              return (
                acc + (item.GrossAmount?.Price || item.GrossPrice?.Price || 0)
              );
            },
            0,
          );

          const netTotal = bookingCart.TotalNetAmount?.Price || total;
          const currency =
            bookingCart.BasketItems[0]?.GrossAmount?.Currency ||
            bookingCart.BasketItems[0]?.GrossPrice?.Currency ||
            "EUR";

          if (total > 0) {
            try {
              const contentIds = bookingCart.BasketItems.map(
                (item: any) => item.Service || item.LineNr?.toString() || "",
              ).filter(Boolean);

              trackAddPaymentInfo({
                content_ids: contentIds,
                currency,
                value: netTotal || total,
              });

              const ga4Items = bookingCart.BasketItems.map(
                mapBookingCartItemToGA4Item,
              );
              trackGA4AddPaymentInfo(
                ga4Items,
                netTotal || total,
                currency,
                "scalapay",
              );
            } catch (error) {
              console.error("Error tracking AddPaymentInfo:", error);
            }
          }
        }

        const clientSessionToken = getCookie("__client-session-token");

        if (!clientSessionToken) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          setMessage(
            t("Si è verificato un errore con la creazione della prenotazione."),
          );
          return;
        }

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
          if (isRedirectingRef.current || allowNavigationRef.current) {
            return null;
          }
          e.preventDefault();
          e.returnValue = "";
          return "";
        };
        beforeUnloadHandlerRef.current = handleBeforeUnload;
        window.addEventListener("beforeunload", handleBeforeUnload);

        setLoadingConfirm(true);

        try {
          const createScalapayResponse = await fetch(
            `/api/checkout/scalapay/create?is_voucher_booking=${isVoucherBooking}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ReferentData: JSON.stringify(bookingCart.ReferentData),
                GuestsData: JSON.stringify(bookingCart.GuestsData),
                invoiceRequested: wantsInvoice,
                billingFirstName: billingFirstName || "",
                billingLastName: billingLastName || "",
                billingLine1,
                billingCity,
                billingPostalCode,
                billingCountry: finalCountry,
                vouchers: bookingCart.vouchers || [],
                basketItems: bookingCart.BasketItems || [],
              }),
            },
          );

          const scalapayData: any = await createScalapayResponse.json();

          if (!createScalapayResponse.ok || !scalapayData?.checkoutUrl) {
            setIsProcessing(false);
            setLoadingConfirm(false);
            submittingRef.current = false;
            if (beforeUnloadHandlerRef.current) {
              window.removeEventListener(
                "beforeunload",
                beforeUnloadHandlerRef.current,
              );
            }
            setMessage(
              scalapayData?.message ||
                t(
                  "Si è verificato un errore con la creazione del pagamento.",
                ),
            );
            return;
          }

          reservationCodeRef.current = scalapayData.reservationCode || null;
          // A hard redirect off-site is about to happen: mirror the 3DS/
          // Stripe-redirect convention (isRedirectingRef) so the pagehide/
          // beacon gift-card release guard elsewhere in this component does
          // not fire while the customer is on scalapay.com. No gift card can
          // still be applied at this point: the guard at the top of this
          // branch already awaited a confirmed release (or found nothing
          // pending) before this new reservation was created, so
          // `reservationCodeRef` was holding no debited-but-unrefunded
          // reservation when it was overwritten just above.
          isRedirectingRef.current = true;
          allowNavigationRef.current = true;
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
          // Removing OUR beforeunload listener is not enough: BookingPage
          // registers its own navigation guards (beforeunload, popstate, Remix
          // navigation, link clicks) for the half-filled-booking case, and they
          // would challenge this handoff with the browser's native "are you
          // sure you want to leave this page?" dialog. Tell it this one is
          // intentional.
          onLeavingForPaymentProvider?.();
          window.location.href = scalapayData.checkoutUrl;
        } catch (error) {
          console.error("Error starting Scalapay checkout:", error);
          captureException(error, {
            level: "error",
            tags: { event: "scalapay_booking_create_failed" },
          });
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
          setMessage(
            t("Si è verificato un errore con la creazione del pagamento."),
          );
        }
      } else {
        const clientSessionToken = getCookie("__client-session-token");

        if (!clientSessionToken) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          setMessage(
            t("Si è verificato un errore con la creazione della prenotazione."),
          );
          return;
        }

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
          if (isRedirectingRef.current || allowNavigationRef.current) {
            return null;
          }
          e.preventDefault();
          e.returnValue = "";
          return "";
        };

        beforeUnloadHandlerRef.current = handleBeforeUnload;
        window.addEventListener("beforeunload", handleBeforeUnload);

        setLoadingConfirm(true);

        if (
          bookingCart &&
          bookingCart.BasketItems &&
          bookingCart.BasketItems.length > 0
        ) {
          const total = bookingCart.BasketItems.reduce(
            (acc: number, item: any) => {
              if (item.Role === "INCLUDED") {
                return acc;
              }
              return (
                acc + (item.GrossAmount?.Price || item.GrossPrice?.Price || 0)
              );
            },
            0,
          );

          const netTotal = bookingCart.TotalNetAmount?.Price || total;
          const currency =
            bookingCart.BasketItems[0]?.GrossAmount?.Currency ||
            bookingCart.BasketItems[0]?.GrossPrice?.Currency ||
            "EUR";

          if (total > 0) {
            try {
              const contentIds = bookingCart.BasketItems.map(
                (item: any) => item.Service || item.LineNr?.toString() || "",
              ).filter(Boolean);

              trackAddPaymentInfo({
                content_ids: contentIds,
                currency,
                value: netTotal || total,
              });

              const ga4Items = bookingCart.BasketItems.map(
                mapBookingCartItemToGA4Item,
              );
              trackGA4AddPaymentInfo(
                ga4Items,
                netTotal || total,
                currency,
                "card",
              );
            } catch (error) {
              console.error("Error tracking AddPaymentInfo:", error);
            }
          }
        }

        // When gift cards are applied the reservation was already created at
        // the first "apply": reuse it. Otherwise keep the original behavior
        // (a fresh reservation for every attempt).
        let reservationCode: string | null =
          giftCards.length > 0 ? reservationCodeRef.current : null;

        if (!reservationCode) {
          const createReservationResponse = await fetch(
            `/api/checkout/create-reservation?is_voucher_booking=${isVoucherBooking}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ReferentData: JSON.stringify(bookingCart.ReferentData),
                GuestsData: JSON.stringify(bookingCart.GuestsData),
                invoiceRequested: wantsInvoice,
                billingFirstName: billingFirstName || "",
                billingLastName: billingLastName || "",
                vouchers: bookingCart.vouchers || [],
                basketItems: bookingCart.BasketItems || [],
                paymentMethodCode: "FULL_ADVANCED_PAYMENT",
              }),
            },
          );

          if (!createReservationResponse.ok) {
            setIsProcessing(false);
            setLoadingConfirm(false);
            submittingRef.current = false;
            setMessage(
              t(
                "Si è verificato un errore con la creazione della prenotazione.",
              ),
            );
            if (beforeUnloadHandlerRef.current) {
              window.removeEventListener(
                "beforeunload",
                beforeUnloadHandlerRef.current,
              );
            }
            return;
          }

          const createReservationData: any =
            await createReservationResponse.json();
          if (createReservationData.status !== "success") {
            setIsProcessing(false);
            setLoadingConfirm(false);
            submittingRef.current = false;
            setMessage(
              createReservationData.message ||
                t(
                  "Si è verificato un errore con la creazione della prenotazione.",
                ),
            );
            if (beforeUnloadHandlerRef.current) {
              window.removeEventListener(
                "beforeunload",
                beforeUnloadHandlerRef.current,
              );
            }
            return;
          }

          reservationCode = createReservationData.reservationCode;
          reservationCodeRef.current = reservationCode;
        }
        isRedirectingRef.current = true;

        if (fullyCoveredByGift) {
          // GIFT CARD ONLY: the advance payment is fully covered by the
          // applied gift cards, no Stripe payment involved. The beforeunload
          // guard stays armed until the confirm outcome is known: the gift
          // cards are already charged at this point.
          const failGiftConfirm = (message?: string) => {
            if (beforeUnloadHandlerRef.current) {
              window.removeEventListener(
                "beforeunload",
                beforeUnloadHandlerRef.current,
              );
            }
            isRedirectingRef.current = false;
            setLoadingConfirm(false);
            setIsProcessing(false);
            submittingRef.current = false;
            setMessage(
              message ||
                t(
                  "Si è verificato un errore con la creazione della prenotazione.",
                ),
            );
          };

          fetch(`/api/checkout/confirmation?is_voucher_booking=${isVoucherBooking}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              reservationCode: reservationCode,
              giftCardPayment: true,
              vouchers: bookingCart.vouchers || [],
            }),
          })
            .then((res) => {
              if (!res.ok) {
                failGiftConfirm();
                return;
              }
              return res.json();
            })
            .then((data: any) => {
              if (!data) return;
              if (data?.status === "succeeded" && data?.reservationId) {
                giftCommittedRef.current = true;
                allowNavigationRef.current = true;
                if (beforeUnloadHandlerRef.current) {
                  window.removeEventListener(
                    "beforeunload",
                    beforeUnloadHandlerRef.current,
                  );
                }
                navigate(
                  `/order/confirmed?reservation-id=${data.reservationId}${
                    data?.advPaymentWarning ? "&adv-payment-warning=true" : ""
                  }`,
                );
              } else {
                failGiftConfirm(data?.message);
              }
            })
            .catch((error) => {
              console.error("Error confirming gift card checkout:", error);
              failGiftConfirm();
            });
          return;
        }

        // MIXED PAYMENT: when gift cards partially cover the advance, Stripe
        // must charge only the remaining amount (validated server-side).
        const useRemaining = giftCards.length > 0 && (giftRemaining ?? 0) > 0;

        if (!stripe || !elements) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          // No redirect will follow: the user stays on this page, so the
          // gift-card release guard must not think one is in flight.
          isRedirectingRef.current = false;
          allowNavigationRef.current = false;
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
          setMessage(
            t("Si è verificato un errore imprevisto con il pagamento."),
          );
          return;
        }

        const createPaymentIntentResponse = await fetch(
          `/api/checkout/create-payment-intent?reservation_code=${reservationCode}&is_voucher_booking=${isVoucherBooking}${
            useRemaining ? "&use_remaining=true" : ""
          }`,
        );

        if (!createPaymentIntentResponse.ok) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          // No redirect will follow: the user stays on this page, so the
          // gift-card release guard must not think one is in flight.
          isRedirectingRef.current = false;
          allowNavigationRef.current = false;
          setMessage(
            t("Si è verificato un errore con la creazione del pagamento."),
          );
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
          return;
        }

        const paymentIntentData: any = await createPaymentIntentResponse.json();
        if (
          !paymentIntentData.clientSecret ||
          !paymentIntentData.paymentIntentId
        ) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          // No redirect will follow: the user stays on this page, so the
          // gift-card release guard must not think one is in flight.
          isRedirectingRef.current = false;
          allowNavigationRef.current = false;
          setMessage(
            t("Si è verificato un errore con la creazione del pagamento."),
          );
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
          return;
        }

        const returnUrl = clientSessionToken
          ? `${
              window.location.origin
            }/api/checkout/confirmation?reservation_code=${reservationCode}&client_session_token=${encodeURIComponent(
              clientSessionToken,
            )}&is_voucher_booking=${isVoucherBooking}${
              useRemaining ? "&gift_cards=true" : ""
            }`
          : null;

        if (!returnUrl) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          // No redirect will follow: the user stays on this page, so the
          // gift-card release guard must not think one is in flight.
          isRedirectingRef.current = false;
          allowNavigationRef.current = false;
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
          setMessage(
            t("Si è verificato un errore con la creazione della prenotazione."),
          );
          return;
        }

        if (beforeUnloadHandlerRef.current) {
          window.removeEventListener(
            "beforeunload",
            beforeUnloadHandlerRef.current,
          );
        }
        isRedirectingRef.current = true;
        // `redirect: "if_required"` below can navigate off-site for a 3DS
        // challenge, so BookingPage's navigation guards have to be disarmed
        // here too, for the same reason as the Scalapay branch above. This one
        // is easier to miss because the redirect only happens for some cards.
        onLeavingForPaymentProvider?.();
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: paymentIntentData.clientSecret,
          confirmParams: {
            return_url: returnUrl,
          },
          redirect: "if_required",
        });

        if (
          paymentIntent?.status === "requires_action" ||
          paymentIntent?.status === "requires_payment_method"
        ) {
          isRedirectingRef.current = true;
          allowNavigationRef.current = true;
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener(
              "beforeunload",
              beforeUnloadHandlerRef.current,
            );
          }
        }

        if (error) {
          setIsProcessing(false);
          setLoadingConfirm(false);
          submittingRef.current = false;
          // confirmPayment resolved (rather than redirecting off-page), so
          // the user is still on this page: the gift-card release guard
          // must not keep treating a redirect as in flight.
          isRedirectingRef.current = false;
          allowNavigationRef.current = false;
          if (
            error.type === "card_error" ||
            error.type === "validation_error"
          ) {
            console.error("Error confirming checkout:", error);
            if (error.type === "validation_error") {
              setMessage(error.message);
            } else {
              setMessage(
                t("Si è verificato un errore imprevisto con il pagamento."),
              );
            }
          } else {
            console.error("Error confirming checkout:", error);
            setMessage(t("Si è verificato un errore imprevisto."));
          }
        } else if (paymentIntent) {
          switch (paymentIntent.status) {
            case "succeeded":
              setLoadingConfirm(true);
              fetch(
                `/api/checkout/confirmation?payment_intent=${paymentIntent.id}&payment_intent_client_secret=${paymentIntent.client_secret}&redirect_status=${paymentIntent.status}&is_voucher_booking=${isVoucherBooking}`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    reservationCode: reservationCodeRef.current,
                    giftCardPayment: useRemaining,
                    vouchers: bookingCart.vouchers || [],
                  }),
                },
              )
                .then((res) => {
                  if (!res.ok) {
                    setLoadingConfirm(false);
                    setIsProcessing(false);
                    submittingRef.current = false;
                    // Stripe already succeeded but our backend confirm
                    // failed: the user stays on this page (possibly with
                    // gift cards still charged on this reservation), so the
                    // redirect guard must not stay stuck.
                    isRedirectingRef.current = false;
                    allowNavigationRef.current = false;
                    setMessage(
                      t(
                        "Si è verificato un errore con la creazione della prenotazione.",
                      ),
                    );
                    return;
                  }
                  return res.json();
                })
                .then((data: any) => {
                  if (!data) return;
                  if (data?.status === "succeeded" && data?.reservationId) {
                    if (
                      isVoucherBooking &&
                      data?.unmatchedVouchers &&
                      data.unmatchedVouchers.length > 0
                    ) {
                      alert(
                        data.warning ||
                          `I seguenti voucher non sono stati utilizzati: ${data.unmatchedVouchers.join(
                            ", ",
                          )}`,
                      );
                    }

                    if (
                      bookingCart?.vouchers &&
                      bookingCart.vouchers.length > 0
                    ) {
                      const basketItems = bookingCart.BasketItems || [];
                      const cartServices =
                        basketItems
                          .filter(
                            (item: any) =>
                              item.Role === "MAIN" ||
                              item.Role === "INTEGRATION",
                          )
                          .map((item: any) => item.Service) || [];

                      const matchedVoucherCodes = new Set<string>();
                      const vouchers = bookingCart.vouchers || [];

                      if (cartServices.length > 0) {
                        cartServices.forEach((serviceCode: string) => {
                          const vouchersForService = vouchers.filter((v) => {
                            if (
                              !v.voucherData.ServicesIncludedCodes ||
                              v.voucherData.ServicesIncludedCodes.length === 0
                            ) {
                              return true;
                            }
                            return v.voucherData.ServicesIncludedCodes.includes(
                              serviceCode,
                            );
                          });

                          if (vouchersForService.length > 0) {
                            const voucherWithShortestExpiration =
                              vouchersForService.reduce((prev, curr) => {
                                const prevExp = prev.voucherData.ExpirationDate
                                  ? new Date(prev.voucherData.ExpirationDate)
                                  : null;
                                const currExp = curr.voucherData.ExpirationDate
                                  ? new Date(curr.voucherData.ExpirationDate)
                                  : null;

                                if (!prevExp) return curr;
                                if (!currExp) return prev;

                                return currExp < prevExp ? curr : prev;
                              });
                            matchedVoucherCodes.add(
                              voucherWithShortestExpiration.code,
                            );
                          }
                        });
                      } else {
                        const genericVouchers = vouchers.filter(
                          (v) =>
                            !v.voucherData.ServicesIncludedCodes ||
                            v.voucherData.ServicesIncludedCodes.length === 0,
                        );

                        if (genericVouchers.length > 0) {
                          const shortestGeneric = genericVouchers.reduce(
                            (prev, curr) => {
                              const prevExp = prev.voucherData.ExpirationDate
                                ? new Date(prev.voucherData.ExpirationDate)
                                : null;
                              const currExp = curr.voucherData.ExpirationDate
                                ? new Date(curr.voucherData.ExpirationDate)
                                : null;

                              if (!prevExp) return curr;
                              if (!currExp) return prev;

                              return currExp < prevExp ? curr : prev;
                            },
                          );
                          matchedVoucherCodes.add(shortestGeneric.code);
                        }
                      }
                    }

                    giftCommittedRef.current = true;
                    navigate(
                      `/order/confirmed?reservation-id=${data.reservationId}${
                        data?.advPaymentWarning
                          ? "&adv-payment-warning=true"
                          : ""
                      }`,
                    );
                  } else if (data?.status === "error") {
                    setLoadingConfirm(false);
                    setIsProcessing(false);
                    submittingRef.current = false;
                    // Same as above: Stripe succeeded, our confirm did not.
                    isRedirectingRef.current = false;
                    allowNavigationRef.current = false;
                    setMessage(
                      data?.message ||
                        t(
                          "Si è verificato un errore con la creazione della prenotazione.",
                        ),
                    );
                  } else {
                    // Caso in cui la risposta non è valida
                    setLoadingConfirm(false);
                    setIsProcessing(false);
                    submittingRef.current = false;
                    isRedirectingRef.current = false;
                    allowNavigationRef.current = false;
                    setMessage(
                      t(
                        "Si è verificato un errore con la creazione della prenotazione.",
                      ),
                    );
                  }
                })
                .catch((error) => {
                  console.error("Error confirming checkout:", error);
                  setLoadingConfirm(false);
                  setIsProcessing(false);
                  submittingRef.current = false;
                  isRedirectingRef.current = false;
                  allowNavigationRef.current = false;
                  setMessage(
                    t(
                      "Si è verificato un errore con la creazione della prenotazione.",
                    ),
                  );
                });
              break;
            case "processing":
              // Payment left pending (e.g. a delayed payment method): no
              // redirect is in flight, the user remains on this page.
              setMessage(t("Your payment is processing."));
              setIsProcessing(false);
              submittingRef.current = false;
              isRedirectingRef.current = false;
              allowNavigationRef.current = false;
              break;
            case "requires_payment_method":
              setMessage(
                t("Your payment was not successful, please try again."),
              );
              setIsProcessing(false);
              submittingRef.current = false;
              isRedirectingRef.current = false;
              allowNavigationRef.current = false;
              break;
            default:
              setMessage(t("Something went wrong."));
              setIsProcessing(false);
              submittingRef.current = false;
              isRedirectingRef.current = false;
              allowNavigationRef.current = false;
              break;
          }
        }
      }
    } catch (err) {
      console.error("error", err);
      setMessage(t("Si è verificato un errore durante il pagamento."));
      setIsProcessing(false);
      submittingRef.current = false;
      // Defense in depth: an unexpected exception (e.g. confirmPayment
      // itself rejecting) also means no redirect is in flight.
      isRedirectingRef.current = false;
      allowNavigationRef.current = false;
    }
  };

  const handleCardChange = (event: any) => {
    setCardError(null);
    if (event.error) {
      setCardError(event.error.message);
    } else {
      setCardError(null);
    }
  };

  return (
    <>
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-6">
        {showOnlyCardInfo && (
          <>
            <CardElement
              onChange={handleCardChange}
              options={{
                classes: {
                  base: "border-[1px] border-gray-300 p-4 rounded-md",
                },
              }}
              id="card-element"
            />
            {cardError && (
              <div className="text-red-500 text-sm mt-2 text-center w-full block">
                {cardError}
              </div>
            )}
          </>
        )}

        {!showOnlyCardInfo && (
          <>
            {giftCardEnabled && !useScalapay && (
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium">{t("Hai una gift card?")}</h4>

                {giftCards.length > 0 && (
                  <ul className="space-y-2">
                    {giftCards.map((card) => {
                      // The server echoes cardCode masked because the
                      // applied list is persisted/mirrored into client state
                      // (see maskCardCode in app/lib/gift-card.server.ts).
                      // The raw code of a card applied in THIS page session
                      // lives only in React state (appliedRawCodeById, never
                      // persisted), so showing it in full here adds no
                      // exposure; after a reload/3DS return the masked code
                      // is the fallback.
                      const displayCode =
                        appliedRawCodeById[card.id] ?? card.cardCode;
                      const balance = giftCardBalances[card.id];
                      return (
                        <li key={card.id} className="text-sm">
                          <div className="flex items-center justify-between">
                            <span>
                              {t("Gift card")} {displayCode}
                            </span>
                            <span className="flex items-center gap-3">
                              <span className="font-medium">
                                -{formatAmount(card.amount, card.currency)}
                              </span>
                              <button
                                type="button"
                                className="text-red-600 underline disabled:opacity-50"
                                disabled={giftCardLoading || isProcessing}
                                onClick={() => handleRemoveGiftCard(card.id)}
                              >
                                {t("Rimuovi")}
                              </button>
                            </span>
                          </div>
                          {balance !== undefined && (
                            <p className="text-xs text-gray-500">
                              {t("Saldo residuo")}:{" "}
                              {formatAmount(balance.amount, balance.currency)}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {giftRemaining !== null && giftRemaining > 0 && (
                  <p className="text-sm text-gray-600">
                    {t("Residuo da pagare con carta")}:{" "}
                    <span className="font-semibold">
                      {formatAmount(giftRemaining)}
                    </span>
                  </p>
                )}

                {giftRemainingBelowMinimum && (
                  <p className="text-sm text-amber-700">
                    {t(
                      "Il residuo da pagare è inferiore al minimo addebitabile con carta: rimuovi una gift card per procedere.",
                    )}
                  </p>
                )}

                {fullyCoveredByGift && (
                  <p className="text-sm text-green-600">
                    {t(
                      "L'importo è interamente coperto dalle gift card applicate.",
                    )}
                  </p>
                )}

                {!fullyCoveredByGift && selectableOwnGiftCards.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="gift-card-select"
                      className="text-sm text-gray-600"
                    >
                      {t("Oppure scegli una delle tue gift card")}
                    </label>
                    <select
                      id="gift-card-select"
                      // Uncontrolled on purpose: picking an option only
                      // pre-fills the code input below (same one used for
                      // manual typing), it does not apply anything by
                      // itself. Resetting the value after each pick lets the
                      // same card be picked again (e.g. after a failed
                      // apply) without the select getting stuck showing it
                      // as already selected.
                      value=""
                      onChange={(e) => {
                        if (e.target.value) setGiftCardCode(e.target.value);
                      }}
                      disabled={giftCardLoading || isProcessing}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50 bg-white"
                    >
                      <option value="">
                        {t("Seleziona una gift card")}
                      </option>
                      {selectableOwnGiftCards.map((card) => {
                        const expiry = formatGiftCardExpiry(card.expiryDate);
                        return (
                          <option key={card.code} value={card.code}>
                            {maskGiftCardCode(card.code)} ·{" "}
                            {formatAmount(card.balance)}
                            {expiry
                              ? ` · ${t("Scade")} ${expiry}`
                              : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {!fullyCoveredByGift && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="gift-card-code"
                      value={giftCardCode}
                      onChange={(e) => setGiftCardCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleApplyGiftCard();
                        }
                      }}
                      placeholder={t("Codice gift card")}
                      disabled={giftCardLoading || isProcessing}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 disabled:opacity-50"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={
                        giftCardLoading || isProcessing || !giftCardCode.trim()
                      }
                      onClick={handleApplyGiftCard}
                    >
                      {giftCardLoading
                        ? t("Verifica in corso") + "..."
                        : t("Applica")}
                    </Button>
                  </div>
                )}

                {giftCardError && (
                  <p className="text-sm text-red-500">{giftCardError}</p>
                )}

                {giftCards.length > 0 && (
                  <p className="text-xs text-gray-500">
                    {t(
                      "Le gift card vengono addebitate subito. Se torni indietro o cambi metodo di pagamento l'addebito viene annullato.",
                    )}
                  </p>
                )}
              </div>
            )}

            <div className={cn(fullyCoveredByGift && "hidden")}>
              <h4 className="font-medium mb-2">
                {t("Indirizzo di fatturazione")}
              </h4>
              <AddressElement
                options={{
                  mode: "billing",
                  display: { name: "split" },
                  defaultValues: user
                    ? {
                        firstName: user.Name || "",
                        lastName: user.Surname || "",
                        address: {
                          line1: user["Address.Address"] || "",
                          city: user["Address.City"] || "",
                          state: user["Address.StateProvince"] || "",
                          postal_code: user["Address.ZipCode"] || "",
                          country: user["Address.Country"] || "",
                        },
                      }
                    : undefined,
                }}
                className="p-2 border rounded-md"
              />
            </div>
            <div className={cn((fullyCoveredByGift || useScalapay) && "hidden")}>
              <h4 className="font-medium mb-2">{t("Dati di pagamento")}</h4>
              <PaymentElement id="payment-element" />
            </div>
            {useScalapay && (
              <p className="text-sm text-gray-500">
                {t(
                  "Il pagamento avviene sul sito di Scalapay: non è richiesta alcuna carta qui.",
                )}
              </p>
            )}
            {SHOW_BOOKING_INVOICE_OPTION && (
              <div
                className={cn(
                  giftCards.length > 0 && "opacity-60 pointer-events-none",
                )}
              >
                <InvoiceFields
                  wantsInvoice={wantsInvoice}
                  onWantsInvoiceChange={(checked) => {
                    // Locked once a gift card is applied: InvoiceRequested was
                    // already sent with the reservation created at apply time
                    // (the CSS lock alone would not stop keyboard toggling).
                    if (giftCards.length > 0) return;
                    setWantsInvoice(checked);
                    // A reservation left over from a previous attempt carries
                    // the old InvoiceRequested: drop it so the next apply
                    // creates a fresh one with the current choice.
                    reservationCodeRef.current = null;
                  }}
                  taxCode=""
                  onTaxCodeChange={() => {}}
                  vatId=""
                  onVatIdChange={() => {}}
                  customerType="private"
                  onCustomerTypeChange={() => {}}
                  companyName=""
                  onCompanyNameChange={() => {}}
                  legalEmailAddress=""
                  onLegalEmailAddressChange={() => {}}
                  interchangeId=""
                  onInterchangeIdChange={() => {}}
                  billingCountry=""
                  taxCodeError={null}
                  formErrors={{}}
                  idPrefix="booking-"
                  infoOnly
                />
              </div>
            )}
            {SHOW_BOOKING_INVOICE_OPTION && giftCards.length > 0 && (
              <p className="text-xs text-gray-500">
                {t(
                  "Per modificare l'opzione fattura rimuovi prima le gift card applicate.",
                )}
              </p>
            )}
          </>
        )}

        <div className="mt-3 border-t-[1px] border-dashed border-t-secondary border-b-secondary py-4 space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="accept-cancellation-policy"
              checked={acceptedCancellationPolicy}
              onCheckedChange={(checked) => {
                setAcceptedCancellationPolicy(checked === true);
              }}
              className="data-[state=checked]:bg-secondary data-[state=checked]:text-white rounded-none"
            />
            <label
              htmlFor="accept-cancellation-policy"
              className="text-lg font-bold cursor-pointer"
            >
              {t("Ho letto e accetto i termini del")}{" "}
              <a
                href="/spa-regulations"
                target="_blank"
                className="text-secondary underline hover:text-secondary/80"
              >
                {t("Regolamento ChorusLife Immersive SPA")}
              </a>
              {" *"}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="accept-sales-conditions"
              checked={acceptedSalesConditions}
              onCheckedChange={(checked) => {
                setAcceptedSalesConditions(checked === true);
              }}
              className="data-[state=checked]:bg-secondary data-[state=checked]:text-white rounded-none"
            />
            <label
              htmlFor="accept-sales-conditions"
              className="text-lg font-bold cursor-pointer"
            >
              {t("Accetto le")}{" "}
              <a
                href="/terms-and-conditions"
                target="_blank"
                className="text-secondary underline hover:text-secondary/80"
              >
                {t("Condizioni Generali di Vendita")}
              </a>{" "}
              {t("di ChorusWellness S.r.l.")}
              {" *"}
            </label>
          </div>
        </div>

        <div className="flex flex-row items-center justify-center space-x-2">
          <Button
            variant={"muted"}
            disabled={giftCardLoading}
            onClick={async () => {
              // Going back abandons the checkout: refund any gift card
              // already charged before leaving.
              if (giftCards.length > 0) {
                await releaseGiftCards();
              }
              onCancel();
            }}
          >
            <span>{t("Indietro")}</span>
          </Button>

          <Button
            type="submit"
            variant="secondary"
            disabled={
              loadingConfirm ||
              isProcessing ||
              // Stripe is not involved when the advance is fully covered by
              // gift cards, nor when Scalapay was chosen: don't block the
              // confirm on Stripe's readiness in either case.
              (!fullyCoveredByGift && !useScalapay && (!stripe || !elements)) ||
              // Also true while releaseGiftCards() is in flight (it toggles
              // this same flag): keeps Submit honestly disabled instead of
              // silently swallowing a click during the release that must
              // complete before a Scalapay reservation can be created.
              giftCardLoading ||
              // Residual below the card minimum: neither payment path can
              // complete, the user must remove a gift card first.
              giftRemainingBelowMinimum ||
              !acceptedCancellationPolicy ||
              !acceptedSalesConditions
            }
            className="py-3 px-10 font-semibold disabled:opacity-50"
          >
            <span id="button-text">
              {!showOnlyCardInfo && !useScalapay && (
                <>
                  {isProcessing
                    ? fullyCoveredByGift
                      ? t("Conferma in corso") + "..."
                      : t("Pagamento in corso") + "..."
                    : fullyCoveredByGift
                      ? t("Conferma prenotazione")
                      : t("Paga ora")}
                </>
              )}
              {useScalapay && (
                <>
                  {isProcessing
                    ? t("Ti stiamo reindirizzando a Scalapay") + "..."
                    : t("Continua con Scalapay")}
                </>
              )}
              {showOnlyCardInfo && (
                <>
                  {isProcessing
                    ? t("Prenotazione in corso") + "..."
                    : t("Conferma prenotazione")}
                </>
              )}
            </span>
          </Button>
        </div>

        {message && (
          <div
            id="payment-message"
            className="text-red-500 text-sm mt-2 text-center w-full block"
          >
            {message}
          </div>
        )}
      </form>

      {loadingConfirm && (
        <div className="fixed top-0 left-0 w-full h-full inset-0 z-50 space-y-4 flex flex-col items-center justify-center bg-white bg-opacity-75 backdrop-blur-sm animate-in fade-in-60">
          <span className="text-xl block mb-4 animate-pulse">
            {t("Sto creando la prenotazione")}...
          </span>
          <LogoIcon className="fill-secondary -ml-4" />
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        </div>
      )}
    </>
  );
}
